import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter';
import { dataGridTextSorter } from '../../../../common/DataGrid/sorters/DataGridSorters';
import DataGrid from '../../../../common/DataGrid/DataGrid';
import { Tag, Progress } from 'antd';
import dayjs from 'dayjs';

const formatDaysLeft = (daysLeft, t) => {
  if (daysLeft === 1) return t('1 deň');
  if (daysLeft >= 2 && daysLeft <= 4)
    return t('{{count}} dni', { count: daysLeft });
  return t('{{count}} dní', { count: daysLeft });
};

const getDeadlineMeta = (deadline, t) => {
  if (!deadline) {
    return { label: t('Bez termínu'), color: 'default', daysLeft: null };
  }

  const parsed = dayjs(deadline);
  if (!parsed.isValid()) {
    return { label: '-', color: 'default', daysLeft: null };
  }

  const today = dayjs().startOf('day');
  const endOfDeadline = parsed.endOf('day');
  const daysLeft = endOfDeadline.diff(today, 'day');

  if (daysLeft < 0) return { label: t('Ukončená'), color: 'default', daysLeft };
  if (daysLeft === 0) return { label: t('Dnes'), color: 'red', daysLeft };
  if (daysLeft < 3)
    return { label: formatDaysLeft(daysLeft, t), color: 'red', daysLeft };
  if (daysLeft < 14)
    return { label: formatDaysLeft(daysLeft, t), color: 'orange', daysLeft };
  return { label: formatDaysLeft(daysLeft, t), color: 'blue', daysLeft };
};

const getDeadlineProgress = (deadline) => {
  if (!deadline) return null;
  const parsed = dayjs(deadline);
  if (!parsed.isValid()) return null;

  const today = dayjs().startOf('day');
  const endOfDeadline = parsed.endOf('day');
  const daysLeft = endOfDeadline.diff(today, 'day');
  if (daysLeft < 0) return { percent: 100, color: '#595959' };

  const totalWindowDays = 30;
  const clampedLeft = Math.min(Math.max(daysLeft, 0), totalWindowDays);
  const percent = Math.round(
    ((totalWindowDays - clampedLeft) / totalWindowDays) * 100,
  );

  let color = '#1677ff';
  if (daysLeft < 3) color = '#ff4d4f';
  else if (daysLeft < 14) color = '#fa8c16';

  return { percent, color };
};

const ErasmusChallengesTable = ({
  erasmusChallenges,
  onDelete,
  onDetailClick,
  onPreviewClick,
}) => {
  const { t } = useTranslation();

  const nameFilterProps = useDataGridTextFilter('name');
  const academicYearFilterProps = useDataGridTextFilter('academic_year');

  const columns = useMemo(
    () => [
      {
        title: t('Názov'),
        dataIndex: 'name',
        ellipsis: true,
        sorter: dataGridTextSorter('name'),
        defaultSortOrder: 'ascend',
        ...nameFilterProps,
      },
      {
        title: t('Termín'),
        dataIndex: 'deadline',
        sorter: dataGridTextSorter('deadline'),
        render: (deadline) =>
          deadline ? new Date(deadline).toLocaleDateString('sk-SK') : '-',
      },
      {
        title: t('Deadline'),
        dataIndex: 'deadline',
        sorter: (a, b) => {
          const leftA =
            getDeadlineMeta(a.deadline, t).daysLeft ?? Number.POSITIVE_INFINITY;
          const leftB =
            getDeadlineMeta(b.deadline, t).daysLeft ?? Number.POSITIVE_INFINITY;
          return leftA - leftB;
        },
        render: (deadline) => {
          const meta = getDeadlineMeta(deadline, t);
          if (meta.label === '-') return '-';
          const progress = getDeadlineProgress(deadline);
          return (
            <div style={{ minWidth: '120px' }}>
              <Tag color={meta.color}>{meta.label}</Tag>
              {progress && (
                <Progress
                  percent={progress.percent}
                  size="small"
                  showInfo={false}
                  strokeColor={progress.color}
                />
              )}
            </div>
          );
        },
      },
      {
        title: t('Konzorcium/Univerzita'),
        dataIndex: 'consortium',
        render: (consortium, record) => {
          if (record.consortium) {
            return record.consortium.name;
          } else if (record.university) {
            return record.university.name;
          }
          return '-';
        },
      },
      {
        title: t('Akademický rok'),
        dataIndex: 'academic_year',
        sorter: dataGridTextSorter('academic_year'),
        ...academicYearFilterProps,
      },
    ],
    [t, nameFilterProps, academicYearFilterProps],
  );

  // Ensure dataSource is always an array
  const dataSource = Array.isArray(erasmusChallenges) ? erasmusChallenges : [];

  return (
    <DataGrid
      rowKey={(challenge) => challenge.id}
      columns={columns}
      dataSource={dataSource}
      permissionsBasic={'erasmus-challenges'}
      onEditClick={onDetailClick}
      onRowClick={onDetailClick}
      onDelete={onDelete}
      onPreviewClick={onPreviewClick}
      rowClickOpensPreview
    />
  );
};

ErasmusChallengesTable.propTypes = {
  erasmusChallenges: PropTypes.array,
  onDelete: PropTypes.func,
  onDetailClick: PropTypes.func,
  onPreviewClick: PropTypes.func,
};

export default ErasmusChallengesTable;
